import { ObservationRecordItem } from "components/utils/ObservationRecordsModal";
import {
  ArticleLocationObservationRecordStockUnit,
  ObservationRecordHistoryEntry,
} from "types";

export type ArticleLocationObservationRecordItem = ObservationRecordItem & {
  stock_unit_ids: number[];
  income_id?: number | null;
  reserved_movement_id?: number | null;
  discharged_movement_id?: number | null;
};

export type GroupedObservationHistoryItem = ObservationRecordHistoryEntry & {
  grouped_count: number;
  group_key: string;
};

const EMPTY_OBSERVATION_KEY = "__empty_observation__";

export const buildObservationRecordItemsFromStockUnits = (
  stockUnits: ArticleLocationObservationRecordStockUnit[],
): ArticleLocationObservationRecordItem[] => {
  const records = new Map<string, ArticleLocationObservationRecordItem>();

  stockUnits.forEach((stockUnit) => {
    const recordKey = [
      stockUnit.status,
      stockUnit.observation ?? EMPTY_OBSERVATION_KEY,
      stockUnit.income_id ?? "no-income",
      stockUnit.reserved_movement_id ?? "no-reserved",
      stockUnit.discharged_movement_id ?? "no-discharged",
    ].join("::");
    const existingRecord = records.get(recordKey);

    if (existingRecord) {
      existingRecord.quantity += 1;
      existingRecord.stock_unit_ids.push(stockUnit.id);
      existingRecord.history.push(...stockUnit.history);
      return;
    }

    records.set(recordKey, {
      id: `${stockUnit.status}-${stockUnit.id}`,
      title: "Observación",
      quantity: 1,
      status: stockUnit.status,
      observation: stockUnit.observation ?? null,
      income_id: stockUnit.income_id ?? null,
      reserved_movement_id: stockUnit.reserved_movement_id ?? null,
      discharged_movement_id: stockUnit.discharged_movement_id ?? null,
      history: [...stockUnit.history],
      stock_unit_ids: [stockUnit.id],
    });
  });

  return Array.from(records.values()).map((record) => ({
    ...record,
    history: [...record.history].sort(
      (left, right) =>
        new Date(right.created_at).getTime() - new Date(left.created_at).getTime(),
    ),
  }));
};

export const buildObservationSummaryFromStockUnits = (
  stockUnits: ArticleLocationObservationRecordStockUnit[],
) => {
  const uniqueObservations = Array.from(
    new Set(
      stockUnits
        .map((stockUnit) => stockUnit.observation?.trim() ?? "")
        .filter(Boolean),
    ),
  );

  if (uniqueObservations.length === 0) {
    return "No asignado";
  }

  return uniqueObservations.join(" - ");
};

export const buildGroupedObservationHistoryItems = (
  history: ObservationRecordHistoryEntry[],
): GroupedObservationHistoryItem[] => {
  const groupedHistory = new Map<string, GroupedObservationHistoryItem>();

  history.forEach((historyItem) => {
    const userId = historyItem.user?.id ?? historyItem.user_id ?? "no-user";
    const createdAt = historyItem.created_at ?? "";
    const previousObservation =
      historyItem.previous_observation ?? "__null_previous__";
    const newObservation = historyItem.new_observation ?? "__null_new__";
    const groupKey = [
      userId,
      createdAt,
      historyItem.observation_status,
      previousObservation,
      newObservation,
    ].join("::");
    const existingGroup = groupedHistory.get(groupKey);

    if (existingGroup) {
      existingGroup.grouped_count += 1;
      return;
    }

    groupedHistory.set(groupKey, {
      ...historyItem,
      grouped_count: 1,
      group_key: groupKey,
    });
  });

  return Array.from(groupedHistory.values()).sort(
    (left, right) =>
      new Date(right.created_at).getTime() - new Date(left.created_at).getTime(),
  );
};
